import React from 'react';
import PropTypes from 'prop-types';
import {
  titleUI,
  yesNoUI,
  yesNoSchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import { replaceStrValues } from 'platform/shared/health-apps';
import { IncomeAssetStatementFormAlert } from '../../../../components/FormAlerts';
import { isRelationshipToVeteran } from '../../../../utils/helpers';
import content from '../../../../utils/content.json';

const h5Style =
  'vads-u-margin-y--1 vads-u-font-family--sans vads-u-font-size--md';

const Description = ({ formData }) => {
  const total = formData?.survivorsBenefitsForm2025VersionEnabled
    ? '$75'
    : '$25';
  return (
    <div>
      <p>
        {replaceStrValues(content['financial-info-income-assets-description'], [
          [total],
        ])}
      </p>
    </div>
  );
};

Description.propTypes = {
  formData: PropTypes.shape({
    survivorsBenefitsForm2025VersionEnabled: PropTypes.bool,
  }),
};

const WhatWeConsiderAsset = () => (
  <va-accordion>
    <va-accordion-item level={4} header="What we consider an asset">
      <div>
        <p>
          Assets include the fair market value of all the real and personal
          property that you own, minus the amount of any mortgages you have.
          “Real property” is the land and buildings you own. And “personal
          property” is items like these:
        </p>
        <ul>
          <li>Investments, like stocks and bonds</li>
          <li>Antique furniture</li>
          <li>Boats</li>
        </ul>
        <h5 className={h5Style}>
          We don’t include items like these in your assets:
        </h5>
        <ul>
          <li>
            Your primary residence (the home where you live most or all of your
            time)
          </li>
          <li>Your car</li>
          <li>
            Basic home items, like appliances that you wouldn’t take with you if
            you moved to a new house
          </li>
        </ul>
      </div>
    </va-accordion-item>

    <va-accordion-item level={4} header="Who we consider a dependent">
      <div>
        <h5 className={h5Style}>A dependent is:</h5>
        <ul>
          <li>
            A spouse (<strong>Note:</strong> We recognize same-sex and common
            law marriages)
          </li>
          <li>
            An unmarried child (including an adopted child or stepchild) who
            meets 1 of the eligibility requirements listed here
          </li>
        </ul>
        <h5 className={h5Style}>
          To be considered a dependent, one of these must be true of an
          unmarried child:
        </h5>
        <ul>
          <li>
            They’re under 18 years old, <strong>or</strong>
          </li>
          <li>
            They’re between the ages of 18 and 23 years old and enrolled in
            school full time, <strong>or</strong>
          </li>
          <li>They became permanently disabled before they turned 18</li>
        </ul>
      </div>
    </va-accordion-item>

    <va-accordion-item level={4} header="Whose assets you need to report">
      <div>
        <h5 className={h5Style}>If you’re a surviving spouse claimant:</h5>
        <p>
          You must report income and assets for yourself and for any child of
          the veteran who lives with you or for whom you are responsible, unless
          a court has decided you do not have custody of the child.
        </p>
        <h5 className={h5Style}>If you’re a surviving child claimant:</h5>
        <p>
          This means that the child isn’t in the custody of a surviving spouse.
          You must report income and assets for yourself, your custodian, and
          your custodian’s spouse. If your custodian is an agency or facility,
          you do not need to report their income.
        </p>
        <h5 className={h5Style}>
          If you’re a custodian of a surviving child claimant who is under 18:
        </h5>
        <p>
          This means that the child is in your custody and isn’t in the custody
          of a surviving spouse. You must report income for yourself, your
          spouse, and any child of the veteran who lives with you or for whom
          you are responsible.
        </p>
      </div>
    </va-accordion-item>
  </va-accordion>
);

/** @type {PageSchema} */
export default {
  uiSchema: {
    ...titleUI(content['financial-info-income-assets-title'], Description),
    'ui:description': WhatWeConsiderAsset,
    totalNetWorth: {
      ...yesNoUI(),
      'ui:options': {
        updateSchema: formData => {
          const total = formData?.survivorsBenefitsForm2025VersionEnabled
            ? '$75'
            : '$25';
          const startingText =
            isRelationshipToVeteran(formData) === 'spouse'
              ? content['financial-info-income-assets-question-prefix-spouse']
              : content['financial-info-income-assets-question-prefix-default'];
          const title = replaceStrValues(
            content['financial-info-income-assets-question'],
            [startingText, total],
          );
          return {
            title,
          };
        },
      },
    },
    incomeAssetStatementFormAlertYes: {
      'ui:description': IncomeAssetStatementFormAlert,
      'ui:options': {
        hideIf: formData => formData?.totalNetWorth !== true,
        displayEmptyObjectOnReview: true,
      },
    },
  },
  schema: {
    type: 'object',
    required: ['totalNetWorth'],
    properties: {
      totalNetWorth: yesNoSchema,
      incomeAssetStatementFormAlertYes: {
        type: 'object',
        properties: {},
      },
    },
  },
};
